import { motion } from "framer-motion";
import {
  Plus,
  Users,
  Globe,
  Calculator,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { Button } from "../ui/button";

interface WelcomeLandingProps {
  onStart: () => void;
}

export function WelcomeLanding({ onStart }: WelcomeLandingProps) {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 50 } as const,
    },
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] w-full max-w-5xl mx-auto px-4 py-8 md:py-16">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="w-full space-y-8 md:space-y-20"
      >
        {/* Hero Section */}
        <div className="text-center space-y-5 md:space-y-8 max-w-2xl mx-auto">
          <motion.div variants={item} className="flex justify-center">
            <div className="bg-primary/10 p-4 rounded-full relative">
              <Sparkles className="h-8 w-8 text-primary absolute -top-1 -right-1 animate-pulse" />
              <Calculator className="h-12 w-12 text-primary" />
            </div>
          </motion.div>

          <motion.div variants={item} className="space-y-4">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/60 bg-clip-text text-transparent pb-2">
              Cuentas claras,
              <br />
              amistades largas.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md mx-auto">
              La forma más elegante de dividir gastos en viajes, cenas y
              proyectos compartidos.
            </p>
          </motion.div>

          <motion.div variants={item}>
            <Button
              size="lg"
              onClick={onStart}
              className="h-14 px-8 text-lg rounded-full shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all hover:scale-105"
            >
              <Plus className="h-5 w-5 mr-2" />
              Crear mi primer evento
            </Button>
          </motion.div>
        </div>

        {/* Feature Grid */}
        <motion.div
          variants={item}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 w-full"
        >
          <FeatureCard
            icon={<Users className="h-6 w-6 text-blue-500" />}
            title="Grupos Ilimitados"
            description="Crea grupos para viajes, compañeros de casa o salidas de fin de semana."
            delay={0.2}
          />
          <FeatureCard
            icon={<Globe className="h-6 w-6 text-purple-500" />}
            title="Multimoneda"
            description="Maneja gastos en cualquier divisa. Nosotros hacemos la conversión."
            delay={0.3}
          />
          <FeatureCard
            icon={<ArrowRight className="h-6 w-6 text-green-500" />}
            title="Balances Simples"
            description="Algoritmos inteligentes para minimizar la cantidad de transferencias."
            delay={0.4}
          />
        </motion.div>
      </motion.div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
  delay,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="p-6 rounded-2xl bg-card border border-border/50 shadow-sm hover:shadow-md transition-all text-center md:text-left"
    >
      <div className="bg-background w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto md:mx-0 shadow-inner">
        {icon}
      </div>
      <h3 className="font-semibold text-lg mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
}
